import "../../../components/common/Tables/DataTable.css"
import React, { useState } from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { PencilIcon, TrashIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id:'status', label:'Status', align:"center"},
  {id:'title', label:'Title', align:null},
  {id:'text', label:'Text', align:null},
  {id:'scheduled_at', label:'Scheduled', align:null},
  {id:'created_at', label:'Created', align:null},
  {id:'author', label:'Author', align:null},
  {id:'edit', label:'Edit', align:"center"},
  {id:'delete', label:'Delete', align:"center"},
];

const unsortable = ['status', 'edit', 'delete'];

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  edit: 60,
  delete: 68,
};

interface TableProps {
  setNotifToModify: any;
  setEditOpen: any;
  setDeleteOpen: any;
  setViewOpen: any;
  // Page controls (Create Notification, search) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const NotificationsTable: React.FC<TableProps> = ({
  setNotifToModify, setEditOpen, setDeleteOpen, setViewOpen, toolbar,
}) => {
  const { data: notifications, loading, meta } = useSelector((state: RootState) => state.notifications);
  const [searchParams, setSearchParams] = useSearchParams();
  // Table Sort (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');
  const navigate = useNavigate();

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const total = meta?.total ?? 0;

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  // Keeps the search and sort (the old pager replaced the whole query with ?page=)
  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  };

  const statusPill = (notif: any) => {
    if (notif.is_sent) return <span className="dt-pill good">Sent</span>;
    if (notif.scheduled_at) return <span className="dt-pill info">Scheduled</span>;
    return <span className="dt-pill caution">Pending</span>;
  };

  const open = (notif: any, setModal: (open: boolean) => void) => (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setNotifToModify(notif);
    setModal(true);
  };

  const pinned = headers.map((header) => header.id).filter((id) => id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const authorName = (notif: any) => notif?.author?.name ?? notif?.author?.username ?? notif?.author?.email;

  const renderCell = (id: string, notif: any) => {
    switch (id) {
      case 'status':
        return statusPill(notif);

      case 'title':
        // A real button so the details open from the keyboard too
        return <button type="button" className="dt-title-btn" onClick={open(notif, setViewOpen)}>{notif?.title}</button>;

      case 'text':
        return <span className="dt-body" title={notif?.text}>{notif?.text}</span>;

      case 'scheduled_at':
        return notif?.scheduled_at && <span className="dt-date">{new Date(notif.scheduled_at).toLocaleString()}</span>;

      case 'created_at':
        return notif?.created_at && <span className="dt-date">{new Date(notif.created_at).toLocaleString()}</span>;

      case 'author':
        return notif?.author?.id ? (
          <a
            href={`/user/${notif.author.id}`}
            className="dt-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/user/${notif.author.id}`);
            }}
          >
            {authorName(notif)}
          </a>
        ) : authorName(notif);

      case 'edit':
        return (
          <Tooltip title="Edit notification" arrow>
            <button type="button" className="dt-act" onClick={open(notif, setEditOpen)} aria-label="Edit notification"><PencilIcon/></button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete notification" arrow>
            <button type="button" className="dt-act danger" onClick={open(notif, setDeleteOpen)} aria-label="Delete notification"><TrashIcon/></button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading notifications"/></div>
        ) : notifications.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {!unsortable.includes(header.id) ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={() => handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {notifications.map((notif: any) => (
                <tr key={notif.id} className="dt-clickable" onClick={open(notif, setViewOpen)}>
                  {headers.map((header) => (
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, notif)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state"><div className="dt-empty-title">No Notifications Found</div></div>
        )}
      </div>

      <DataTableFoot
        shown={notifications?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Notification' : 'Notifications'}
        page={page}
        totalPages={meta?.total_pages ?? 1}
        loading={loading}
        onPage={goToPage}
      />
    </div>
  );
};

export default NotificationsTable;
